import {
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import classnames from 'classnames';
import { useEffect, useState, useRef, type ReactNode } from 'react';
import { hexToRgba } from 'utils/color';
import Co2UniversalButton from '../Co2UniversalButton';

export type ModalMessageTone = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary';

interface ModalMessageBoxProps {
    open: boolean;
    type: string;
    toggle(): void;
    onConfirm?(value?: string): void;
    onClose?(): void | null;
    title?: ReactNode;
    description?: ReactNode;
    question?: ReactNode;
    inputLabel?: string;
    initialValue?: string;
    icon?: string;
    tone?: ModalMessageTone;
}

const TONE_COLORS: Record<ModalMessageTone, string> = {
    primary: '#50c3e6',
    info: '#50c3e6',
    success: '#0ab39c',
    warning: '#ff9933',
    danger: '#f06548',
    secondary: '#74788d',
};

const TYPE_THEME: Record<string, { label: string; icon: string; tone: ModalMessageTone }> = {
    question: { label: 'Question', icon: 'fa-circle-question', tone: 'primary' },
    error: { label: 'Error', icon: 'fa-desktop', tone: 'danger' },
    info: { label: 'Information', icon: 'fa-circle-info', tone: 'info' },
    warning: { label: 'Warning', icon: 'fa-circle-info', tone: 'warning' },
    edit: { label: 'Edit', icon: 'fa-pen-to-square', tone: 'secondary' },
};

const hasContent = (node: ReactNode) => {
    if (node == null || node === false) return false;
    if (typeof node === 'string') return node.trim() !== '';
    return true;
};

const mixHex = (hex: string, channel: number, amount: number) => {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    const mix = (c: number) => Math.round(c + (channel - c) * amount);
    return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

const resolveIconClass = (icon: string) => {
    const trimmed = icon.trim().replace(/\bfa-light\b/g, 'fa-regular');
    if (trimmed.includes('fa-')) {
        return trimmed.startsWith('fa ') ? trimmed : `fa fa-regular ${trimmed.replace(/^fa-regular\s+/, '')}`;
    }
    return `fa fa-regular ${trimmed}`;
};

const ModalMessageBox: React.FC<ModalMessageBoxProps> = ({
    open,
    type,
    toggle,
    onConfirm,
    title,
    description,
    question,
    onClose,
    inputLabel,
    initialValue,
    icon,
    tone,
}) => {
    const [value, setValue] = useState(initialValue || '');
    const [error, setError] = useState<string | undefined>(undefined);

    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    useEffect(() => {
        setValue(initialValue || '');
        setError(undefined);
    }, [initialValue, open]);

    const theme = TYPE_THEME[type] || TYPE_THEME.info;
    const resolvedTone = tone || theme.tone;
    const typeColor = TONE_COLORS[theme.tone];
    const color = TONE_COLORS[resolvedTone];
    const illustrationIcon = resolveIconClass(theme.icon);
    const showIllustration = type !== 'edit';
    const questionColor =
        type === 'error' || resolvedTone === 'danger'
            ? TONE_COLORS.danger
            : type === 'warning' || resolvedTone === 'warning'
                ? TONE_COLORS.warning
                : color;

    const dismiss = () => {
        toggle();
        onClose?.();
    };

    return (
        <Modal
            isOpen={open}
            centered
            toggle={dismiss}
            className="border-0 modal-message-box"
            modalClassName="modal fade zoomIn p-3"
            contentClassName={showIllustration ? 'overflow-hidden' : undefined}
            backdrop="static"
            onOpened={() => {
                inputRef.current?.focus();
            }}
        >
            <ModalHeader toggle={dismiss} className={showIllustration ? 'border-0 pb-3' : 'border-bottom pb-3'}>
                <div className="d-flex align-items-center">
                    <div className="avatar-xs flex-shrink-0">
                        <span
                            className="avatar-title rounded fs-14"
                            style={{ backgroundColor: hexToRgba(typeColor, 0.1) }}
                        >
                            <i className={resolveIconClass(theme.icon)} style={{ color: typeColor }} />
                        </span>
                    </div>
                    <span className="fs-14 ms-2" style={{ fontWeight: 400 }}>
                        {theme.label}
                    </span>
                </div>
            </ModalHeader>
            <ModalBody className="modal-body px-4 pb-3 pt-1">
                <div className="text-center">
                    {showIllustration && (
                        <div
                            className="modal-message-banner"
                            style={{
                                ['--mm-color' as string]: color,
                                ['--mm-badge' as string]: hexToRgba(color, 0.2),
                                ['--mm-arc-0' as string]: mixHex(color, 0, 0.48),
                                ['--mm-arc-1' as string]: mixHex(color, 0, 0.34),
                                ['--mm-arc-2' as string]: mixHex(color, 0, 0.16),
                                ['--mm-arc-3' as string]: color,
                                ['--mm-arc-4' as string]: mixHex(color, 255, 0.38),
                                ['--mm-arc-5' as string]: mixHex(color, 255, 0.62),
                            }}
                        >
                            <div className="modal-message-banner-art" aria-hidden>
                                <svg
                                    className="modal-message-banner-ripple"
                                    viewBox="0 0 480 112"
                                    preserveAspectRatio="xMidYMax slice"
                                >
                                    <rect width="480" height="112" fill="var(--mm-arc-0)" />
                                    <circle cx="240" cy="112" r="210" fill="var(--mm-arc-1)" />
                                    <circle cx="240" cy="112" r="158" fill="var(--mm-arc-2)" />
                                    <circle cx="240" cy="112" r="116" fill="var(--mm-arc-3)" />
                                    <circle cx="240" cy="112" r="78" fill="var(--mm-arc-4)" />
                                    <circle cx="240" cy="112" r="44" fill="var(--mm-arc-5)" />
                                </svg>
                            </div>
                            <span className="modal-message-illustration-ring">
                                <span className="modal-message-illustration-badge">
                                    <i className={illustrationIcon} />
                                </span>
                            </span>
                        </div>
                    )}

                    {hasContent(title) && (
                        <div className="fs-14 fw-semibold text-body mb-1">{title}</div>
                    )}
                    {hasContent(description) && (
                        <div className="fs-14 mb-1">{description}</div>
                    )}
                    {hasContent(question) && (
                        <div
                            className="fs-14 my-3"
                        >
                            {question}
                        </div>
                    )}
                    {inputLabel && (
                        <div className="text-start mt-3">
                            <Label
                                className={classnames({
                                    'form-label': true,
                                    'text-danger': error != undefined,
                                })}
                            >
                                {error ? error : inputLabel}
                            </Label>
                            <Input
                                type="text"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                innerRef={inputRef}
                                autoFocus
                            />
                        </div>
                    )}
                    {type === 'edit' && (
                        <Input
                            type="textarea"
                            rows={3}
                            className="mt-3"
                            style={{ resize: 'none' }}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            innerRef={inputRef}
                            autoFocus
                        />
                    )}
                </div>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between border-top py-2">
                {type === 'edit' && (
                    <Co2UniversalButton
                        onClick={dismiss}
                        iconClass="fa fa-regular fa-xmark"
                    >
                        Close
                    </Co2UniversalButton>
                )}

                {(type == 'error' ||
                    type == 'info' ||
                    type == 'edit') && <div></div>}

                {type !== 'edit' && (
                    <Co2UniversalButton
                        onClick={dismiss}
                        iconPosition={(type == 'error' || type == 'info') ? 'right' : 'left'}
                        iconClass="fa fa-regular fa-xmark"
                    >
                        {type == 'question' || type == 'warning' ? 'No' : 'OK'}
                    </Co2UniversalButton>
                )}

                {(type == 'question' ||
                    type == 'warning' ||
                    type == 'edit') && (
                        <Co2UniversalButton
                            iconPosition="right"
                            variant="primary"
                            iconClass="fa fa-regular fa-circle-check"
                            onClick={() => {
                                const confirmedValue = inputLabel ? value.trim() : value;
                                if (inputLabel && !confirmedValue) {
                                    setError(inputLabel + ' is required');
                                    return;
                                }
                                setError(undefined);
                                toggle();
                                onConfirm?.(confirmedValue);
                            }}
                        >
                            {type == 'edit' ? 'Save' : 'Yes'}
                        </Co2UniversalButton>
                    )}
            </ModalFooter>
        </Modal>
    );
};

export default ModalMessageBox;
